import { type SiteTipId } from '@/content/tips';

/**
 * Which tip a page shows, and which pages show none.
 *
 * Two halves and no logic, the same shape as `config/whatsappContacts.ts`:
 * this file is the decision, `utils/pageTip.ts` is the matching, and
 * `hooks/useScrollNudge.ts` is the React on top. Moving a tip to another page
 * is one line here and nothing anywhere else.
 */

/**
 * Path → tip.
 *
 * Keys are matched by path segment, longest first, so a key covers a page and
 * everything under it: `/cars` would carry `/cars/toyota/premio`, and a deeper
 * key overrides a shallower one. Dynamic segments are not written here — the
 * map is matched against real pathnames, so `/vehicles` is the key for
 * `/vehicles/:slug`.
 *
 * `/` is the one key that does **not** work that way: it matches the home page
 * exactly and nothing beneath it. A prefix key of `/` would cover every path
 * on the site, which is the reason `whatsappRouting` leaves it out altogether;
 * here the home page genuinely is the busiest page on the site and has to be
 * nameable, so the matcher special-cases it instead. See `utils/pageTip.ts`.
 *
 * ## One key, one tip
 *
 * The map is one-to-one as of 2026-10-25, and that is a decision rather than a
 * coincidence. Three tips used to cover these sixteen routes, grouped by what
 * the visitor was assumed to be doing — and because dismissal follows the page
 * (`hooks/useScrollNudge.ts`), closing the shared cost tip on the home page
 * also silenced the listings and both calculators. From the visitor's side
 * that is a site that has broken, not one that has listened.
 *
 * Writing one message per page fixes that by construction and is the better
 * copy anyway: somebody on the tax calculator and somebody on the accessories
 * page have different questions, and a card general enough to answer both is
 * an advert.
 *
 * A key still covers everything beneath it, so `/services` answers for its
 * service pages and `/import-guide` for its articles. That is the one place
 * where a page's tip is not written for that exact page, and it is why both of
 * those tips are written to suit the section rather than the index.
 */
export const pageTipRouting: Readonly<Record<string, SiteTipId>> = {
  '/': 'home',
  '/cars': 'cars',
  '/vehicles': 'vehicle',
  '/tax-calculator': 'tax-calculator',
  '/landed-cost-calculator': 'landed-cost-calculator',
  '/about': 'about',
  '/services': 'services',
  '/how-it-works': 'how-it-works',
  '/clients': 'clients',
  '/reviews': 'reviews',
  '/destinations': 'destinations',
  /* Covers /deliveries/gallery, and the /deliveries redirect above it. */
  '/deliveries': 'deliveries',
  '/import-a-car': 'import-a-car',
  '/import-guide': 'import-guide',
  '/accessories': 'accessories',
  '/contact': 'contact',
};

/**
 * Paths that are never nudged, whatever the map above says.
 *
 * Checked first and matched by prefix, so `/cars/checkout` also covers
 * `/cars/checkout/…`. Suppression beating the routing map is what makes
 * this list safe to trust: a page listed here cannot be nudged by accident
 * later, however the map above is edited.
 *
 * Three kinds of page are on it, for three different reasons.
 *
 * **A form in progress.** /quote, /cars/cart and /cars/checkout are the pages where the
 * visitor is already doing the thing every tip is trying to talk them into. A
 * card sliding over a half-filled form is not a prompt, it is an obstacle —
 * and the one place on this site where an interruption can cost a real lead
 * rather than a little goodwill. (Phase 3 extends this to any page while a
 * field has focus, which covers the search form on pages that are not listed
 * here; `hooks/useFieldFocused.ts` already knows how to tell.)
 *
 * **The tip's own destination.** /free-import-advice is where `before-you-buy`
 * sends people. Offering a visitor the page they are reading reads as a site
 * that is not paying attention.
 *
 * **Pages that are not part of the sale.** The legal pages and the 404 have a
 * reader who came for one specific thing, and marketing copy on a privacy
 * policy is its own kind of answer about how seriously the policy is meant.
 * The 404 is not listed because it has no fixed path — it is handled by the
 * matcher, which only ever shows a tip for a path the map names.
 */
export const tipSuppressedPaths: readonly string[] = [
  '/quote',
  '/free-import-advice',
  '/cars/cart',
  '/cars/checkout',
  '/cars/submitted',
  '/privacy',
  '/terms',
];

/**
 * How far a visitor must scroll before a tip may appear, in pixels.
 *
 * A distance and not a percentage, and small — this is "they have started
 * reading", not "they have finished". 240px is roughly one flick of a mouse
 * wheel or one thumb swipe: enough that the tip is plainly a response to
 * something the visitor did, and far too early to be mistaken for a popup
 * that fired on arrival.
 *
 * It replaced a 55% depth threshold on 2026-10-24. That number came from
 * popup-vendor guidance about *full-page* interstitials, and applying it to a
 * 360px card in the corner of a desktop window bought nothing: on a page as
 * long as the home page it meant a visitor had to reach the client logo wall
 * before the site said anything, by which point the tip is answering a
 * question they have stopped asking. Google's intrusive-interstitial guidance
 * — the thing a late trigger was really protecting against — is about content
 * that covers the main content on arrival, and specifically about mobile. A
 * corner card that cannot render until a scroll event has fired is not that.
 *
 * It is also the floor that keeps short pages quiet: a page with less than
 * 240px of scroll in it never reaches this, so no tip appears on a page that
 * gave the visitor no room to show interest.
 */
export const TIP_START_PX = 240;

/**
 * The phone case, which is the one that genuinely can be disruptive.
 *
 * On a phone the card is a sheet across the bottom of a small screen, and the
 * same early trigger that is unobtrusive on a desktop is content disappearing
 * under a panel mid-sentence. So a phone gets the tip in two stages: the peek
 * bar arrives at `TIP_START_PX` with everything else, and it opens into the
 * full card either when the visitor taps it or when they pass this depth.
 *
 * That is the collapsed-sheet pattern — Material's small state that expands,
 * the mini-player every maps and music app uses — and it is the published
 * answer to exactly this problem: keep the underlying content usable, stay one
 * tap from the whole thing. The visitor is never shown a panel they did not
 * either ask for or earn.
 *
 * 0.5 rather than the old 0.55 for no deep reason beyond its being the number
 * the mobile guidance keeps naming as a starting point for testing.
 */
export const TIP_PHONE_OPEN_DEPTH = 0.5;

/**
 * How many tips a visitor may close before the rest of the session goes quiet.
 *
 * Two closes in a row is an answer, and continuing to ask after it is how a
 * site earns the reputation this feature is trying to avoid. It resets when a
 * visitor acts on a tip, and — this is the part that matters — it resets when
 * the tab closes: the counter lives in `sessionStorage`, so somebody who was
 * not interested this afternoon is asked again tomorrow. The tips carry
 * information worth having, so nothing here silences them permanently, and
 * nothing in this feature writes to `localStorage`.
 */
export const TIP_DISMISSAL_LIMIT = 2;

/**
 * How long each slide of a deck holds before the next one takes its place.
 *
 * Every page now carries three messages rather than one
 * (`content/tips.ts`), and they are shown in sequence in the one card. Seven
 * seconds is the figure that comes out of the two constraints either side of
 * it. The floor is reading speed: the body copy runs to about 25 words, which
 * at an unhurried 200 words a minute is roughly 7.5 seconds to read the whole
 * slide including its headline — and a slide that turns before it has been
 * read is worse than a slide that was never shown, because the visitor now
 * knows they missed something. The ceiling is the length of a visit: a deck
 * that takes half a minute to get through is a deck nobody sees the end of.
 *
 * Seven is a touch under the reading figure on purpose. The card is not a page
 * of prose to be studied — the headline carries the message and the body
 * qualifies it, so a visitor who has taken the headline has taken the slide.
 * Anyone who wants the whole of one can stop the rotation, and the control
 * that lets them is two pixels from the words.
 *
 * It is deliberately **not** the 5 seconds named in WCAG 2.2.2. Five seconds
 * is the threshold above which moving content must be pausable, not a
 * recommended interval — rotating faster to duck the criterion would mean
 * rotating faster than anyone can read, which is the opposite of the point.
 * The pause control is how this feature satisfies 2.2.2, and
 * `hooks/useTipCarousel.ts` adds the two things the criterion does not ask for
 * and visitors expect anyway: rotation suspends while the pointer or the
 * keyboard is on the card, and stops for good the moment a visitor steers the
 * deck themselves.
 */
export const TIP_SLIDE_MS = 7000;
